import React, { useState } from "react";
import { format } from "date-fns";
import { it as itL, es as esL, fr as frL, enGB } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useSite } from "@/i18n/SiteContext";

export const LOCALES = { en: enGB, it: itL, es: esL, fr: frL };

export const toISODate = (d) => (d ? format(d, "yyyy-MM-dd") : "");
const fromISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

/*
 * availability (optional): { full: [iso], closed: [iso], remaining: {iso: n} }
 * Full and closed dates are disabled; dates with few seats get a small dot.
 */
export const DatePicker = ({ value, onChange, placeholder, testId, minDate, className = "", availability, showLegend = false }) => {
  const { lang, t } = useSite();
  const [open, setOpen] = useState(false);
  const locale = LOCALES[lang] || enGB;

  const full = (availability?.full || []).map(fromISO);
  const closed = (availability?.closed || []).map(fromISO);
  const few = Object.keys(availability?.remaining || {}).map(fromISO);
  const disabled = [...(minDate ? [{ before: minDate }] : []), ...full, ...closed];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`w-full h-12 px-4 flex items-center gap-3 bg-white border border-sand rounded-xl text-sm text-left hover:border-sea focus:outline-none focus-visible:ring-2 focus-visible:ring-sand-brand ${value ? "text-ocean" : "text-ocean/45"} ${className}`}
          style={{ transition: "border-color .2s ease" }}
        >
          <CalendarDays size={16} className="text-sea shrink-0" />
          {value ? format(value, "EEE d MMM yyyy", { locale }) : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start" data-testid={testId ? `${testId}-calendar` : undefined}>
        <Calendar
          mode="single"
          selected={value}
          onSelect={(d) => {
            onChange(d);
            setOpen(false);
          }}
          locale={locale}
          disabled={disabled.length ? disabled : undefined}
          modifiers={{ full, closed, few }}
          modifiersClassNames={{ full: "day-full", closed: "day-closed", few: "day-few" }}
          initialFocus
        />
        {showLegend && (
          <div className="px-4 pb-4 pt-1 flex flex-wrap gap-x-4 gap-y-2 text-[11px] text-ocean/65 border-t border-sand/60" data-testid="calendar-legend">
            <span className="inline-flex items-center gap-1.5 pt-3"><span className="w-3 h-3 rounded-sm bg-white border border-sand" />{t("avail.free")}</span>
            <span className="inline-flex items-center gap-1.5 pt-3"><span className="w-1.5 h-1.5 rounded-full bg-sea" />{t("avail.few")}</span>
            <span className="inline-flex items-center gap-1.5 pt-3"><span className="w-3 h-3 rounded-sm day-full" />{t("avail.full")}</span>
            <span className="inline-flex items-center gap-1.5 pt-3"><span className="w-3 h-3 rounded-sm day-closed" />{t("avail.closed")}</span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
